import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Package } from 'lucide-react'
import { formatNIS } from '@/utils/currency'

interface Plan {
  id: string
  name: string
  name_he: string | null
  plan_code: string
  plan_level: number
  description: string | null
  description_he: string | null
  price_monthly: number
  price_yearly: number
  currency: string
  max_users: number | null
  max_contacts: number | null
  modules_included: string[] | null
  is_active: boolean
  included_users: number
  extra_user_price_monthly: number
  wa_devices_included: number | null
  storage_gb: number
  ai_credits_monthly: number
  max_emails_monthly: number
  max_sms_monthly: number
  max_forms: number | null
  max_academy_courses: number | null
  max_academy_students: number | null
  academy_storage_mb: number
  max_pipelines: number | null
  has_automations: boolean
  has_email_marketing: boolean
  has_comm_automations: boolean
  has_roles_permissions: boolean
  has_branches: boolean
  has_voip: boolean
  has_api_access: boolean
  portal_branding: string
  ai_level: string
  upgrade_to: string | null
}

function lim(v: number | null | undefined): string {
  if (v == null) return '∞'
  return String(v)
}

function storageLabel(gb: number | null | undefined): string {
  if (gb == null) return '∞'
  return `${gb} GB`
}

function planAccent(planCode: string): { bar: string; ring: string } {
  const c = planCode.toLowerCase()
  if (c === 'basic' || c === 'starter') {
    return { bar: 'bg-indigo-500', ring: 'ring-indigo-500/25' }
  }
  if (c === 'pro') {
    return { bar: 'bg-purple-500', ring: 'ring-purple-500/25' }
  }
  if (c === 'business') {
    return { bar: 'bg-emerald-500', ring: 'ring-emerald-500/25' }
  }
  return { bar: 'bg-indigo-500', ring: 'ring-indigo-500/25' }
}

function aiLevelBadgeClass(level: string | null | undefined): string {
  const l = (level ?? '').toLowerCase()
  if (l.includes('advisor')) {
    return 'bg-blue-500/20 text-blue-400 border-blue-500/40'
  }
  if (l.includes('strategist')) {
    return 'bg-purple-500/20 text-purple-300 border-purple-500/40'
  }
  if (l.includes('operator')) {
    return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
  }
  return 'bg-gray-500/15 text-gray-400 border-gray-600/50'
}

function FlagChip({ label, on }: { label: string; on: boolean }) {
  return (
    <span
      className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium border ${
        on ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/35' : 'bg-gray-500/10 text-gray-500 border-gray-600/40'
      }`}
    >
      {label}
    </span>
  )
}

function LimitCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] sm:text-xs truncate" style={{ color: '#6b7280' }}>
        {label}
      </div>
      <div className="text-sm text-white font-medium tabular-nums mt-0.5">{value}</div>
    </div>
  )
}

export default function PlansPage() {
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [toastMsg, setToastMsg] = useState('')

  const showToast = (msg: string) => {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(''), 3000)
  }

  useEffect(() => {
    supabase
      .from('subscription_plans')
      .select('*')
      .order('plan_level')
      .then(({ data }) => {
        setPlans((data ?? []) as Plan[])
        setLoading(false)
      })
  }, [])

  const toggleActive = async (plan: Plan) => {
    const { error } = await supabase
      .from('subscription_plans')
      .update({ is_active: !plan.is_active })
      .eq('id', plan.id)
    if (!error) {
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_active: !p.is_active } : p)))
      showToast(`Plan "${plan.name}" ${!plan.is_active ? 'activated' : 'deactivated'}`)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {toastMsg && (
        <div
          className="fixed top-16 lg:top-6 right-4 z-50 px-5 py-3 rounded-xl text-white text-sm shadow-xl"
          style={{ background: '#22c55e' }}
        >
          {toastMsg}
        </div>
      )}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-white">Subscription Plans</h1>
        <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>
          {plans.length} plans configured
        </p>
      </div>

      {plans.length === 0 ? (
        <div className="rounded-xl py-12 text-center text-sm" style={{ background: '#252836', border: '1px solid #2d3148', color: '#6b7280' }}>
          No plans found
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const accent = planAccent(plan.plan_code)
            return (
              <div
                key={plan.id}
                className={`rounded-xl overflow-hidden ring-1 ring-inset ${accent.ring}`}
                style={{ background: '#252836', border: '1px solid #2d3148' }}
              >
                <div className={`h-1 w-full ${accent.bar}`} />
                <div className="p-4 sm:p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: '#6366f120' }}
                      >
                        <Package size={16} style={{ color: '#a5b4fc' }} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-white font-semibold leading-tight">{plan.name}</div>
                        {plan.name_he ? (
                          <div className="text-sm mt-0.5" style={{ color: '#9ca3af' }}>
                            {plan.name_he}
                          </div>
                        ) : null}
                        <code
                          className="inline-block text-[10px] sm:text-xs px-1.5 py-0.5 rounded mt-1"
                          style={{ background: '#1a1d27', color: '#a5b4fc' }}
                        >
                          {plan.plan_code}
                        </code>
                        <div className="text-[11px] mt-1" style={{ color: '#6b7280' }}>
                          Level {plan.plan_level}
                        </div>
                      </div>
                    </div>
                    <span
                      className="px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0"
                      style={{
                        color: plan.is_active ? '#22c55e' : '#6b7280',
                        background: plan.is_active ? '#22c55e20' : '#6b728020',
                      }}
                    >
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-lg font-bold text-white">{formatNIS(plan.price_monthly ?? 0)}</span>
                    <span className="text-xs" style={{ color: '#6b7280' }}>
                      /mo
                    </span>
                    <span className="text-sm" style={{ color: '#9ca3af' }}>
                      {formatNIS(plan.price_yearly ?? 0)}/yr
                    </span>
                  </div>

                  {plan.description_he ? (
                    <p className="text-sm leading-relaxed" style={{ color: '#9ca3af' }}>
                      {plan.description_he}
                    </p>
                  ) : null}

                  <div>
                    <div className="text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>
                      Limits
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                      <LimitCell label="Users" value={lim(plan.max_users)} />
                      <LimitCell label="WA devices" value={lim(plan.wa_devices_included)} />
                      <LimitCell label="Storage" value={storageLabel(plan.storage_gb)} />
                      <LimitCell label="AI credits / mo" value={lim(plan.ai_credits_monthly)} />
                      <LimitCell label="Emails / mo" value={lim(plan.max_emails_monthly)} />
                      <LimitCell label="SMS / mo" value={lim(plan.max_sms_monthly)} />
                      <LimitCell label="Contacts" value={lim(plan.max_contacts)} />
                      <LimitCell label="Forms" value={lim(plan.max_forms)} />
                      <LimitCell label="Academy courses" value={lim(plan.max_academy_courses)} />
                      <LimitCell label="Academy students" value={lim(plan.max_academy_students)} />
                      <LimitCell label="Pipelines" value={lim(plan.max_pipelines)} />
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>
                      Features
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <FlagChip label="Automations" on={!!plan.has_automations} />
                      <FlagChip label="Email marketing" on={!!plan.has_email_marketing} />
                      <FlagChip label="Comm automations" on={!!plan.has_comm_automations} />
                      <FlagChip label="Roles & permissions" on={!!plan.has_roles_permissions} />
                      <FlagChip label="Branches" on={!!plan.has_branches} />
                      <FlagChip label="VoIP" on={!!plan.has_voip} />
                      <FlagChip label="API access" on={!!plan.has_api_access} />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-medium border ${aiLevelBadgeClass(plan.ai_level)}`}
                    >
                      AI: {plan.ai_level || '—'}
                    </span>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium border"
                      style={{ color: '#c4b5fd', background: '#6366f115', borderColor: '#6366f140' }}
                    >
                      Portal: {plan.portal_branding || '—'}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleActive(plan)}
                    className="w-full py-2.5 rounded-lg text-sm font-medium transition"
                    style={{
                      background: plan.is_active ? '#ef444420' : '#22c55e20',
                      color: plan.is_active ? '#f87171' : '#22c55e',
                      border: `1px solid ${plan.is_active ? '#ef444440' : '#22c55e40'}`,
                    }}
                  >
                    {plan.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
